import { useContext, useEffect } from 'react';
import { FaCode, FaTrophy, FaChartLine, FaUsers, FaFire, FaBullseye } from 'react-icons/fa';
import { ThemeContext } from '../context/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import Footer from '../components/home/Footer';
import FeedbackSection from '../components/about/FeedbackSection';

const About = () => {
  const { theme } = useContext(ThemeContext);
  const { currentUser, loading } = useAuth();

  useEffect(() => {
    document.title = 'CodeBattles - About Us';
  }, []);

  return (
    <div className="min-h-screen" style={{ backgroundColor: theme.background.primary, color: theme.text.primary }}>
      {/* Hero Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-5xl text-center">
          <h1 className="text-5xl md:text-6xl font-extrabold mb-6 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            About CodeBattles
          </h1>
          <p className="text-xl md:text-2xl mb-8" style={{ color: theme.text.secondary }}>
            CodeBattles aims to make coding practice competitive, consistent, and skill-oriented.
          </p>
        </div>
      </section>

      {/* What is CodeBattles */}
      <section className="py-16 px-4" style={{ backgroundColor: theme.background.secondary }}>
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: theme.text.primary }}>
              What is CodeBattles?
            </h2>
            <p className="text-lg" style={{ color: theme.text.secondary }}>
              CodeBattles is a competitive programming platform where users solve coding problems and compete in challenges.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center p-6 rounded-xl" style={{ backgroundColor: theme.background.primary }}>
              <FaCode className="text-4xl text-blue-500 mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-2" style={{ color: theme.text.primary }}>Practice</h3>
              <p style={{ color: theme.text.secondary }}>Solve coding problems across multiple difficulty levels</p>
            </div>
            <div className="text-center p-6 rounded-xl" style={{ backgroundColor: theme.background.primary }}>
              <FaTrophy className="text-4xl text-yellow-500 mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-2" style={{ color: theme.text.primary }}>Challenge</h3>
              <p style={{ color: theme.text.secondary }}>Compete in live and timed coding challenges</p>
            </div>
            <div className="text-center p-6 rounded-xl" style={{ backgroundColor: theme.background.primary }}>
              <FaChartLine className="text-4xl text-green-500 mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-2" style={{ color: theme.text.primary }}>Ranking</h3>
              <p style={{ color: theme.text.secondary }}>Track progress through leaderboards and badges</p>
            </div>
          </div>
        </div>
      </section>

      {/* Purpose */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: theme.text.primary }}>
              Why CodeBattles Exists
            </h2>
            <p className="text-lg max-w-3xl mx-auto" style={{ color: theme.text.secondary }}>
              To make coding practice engaging, help users develop real-world problem-solving skills, 
              and create a competitive environment that motivates consistent growth.
            </p>
          </div>
        </div>
      </section>

      {/* Core Features */}
      <section className="py-16 px-4" style={{ backgroundColor: theme.background.secondary }}>
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: theme.text.primary }}>
              What You Can Do
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="p-6 rounded-xl" style={{ backgroundColor: theme.background.primary }}>
              <h3 className="text-xl font-bold mb-3 flex items-center gap-2" style={{ color: theme.text.primary }}>
                <FaCode className="text-blue-500" />
                Solve Coding Problems
              </h3>
              <p style={{ color: theme.text.secondary }}>
                Access a wide range of problems with automated test case evaluation
              </p>
            </div>
            
            <div className="p-6 rounded-xl" style={{ backgroundColor: theme.background.primary }}>
              <h3 className="text-xl font-bold mb-3 flex items-center gap-2" style={{ color: theme.text.primary }}>
                <FaTrophy className="text-yellow-500" />
                Participate in Challenges
              </h3>
              <p style={{ color: theme.text.secondary }}>
                Join live and timed challenges to compete with other developers
              </p>
            </div>
            
            <div className="p-6 rounded-xl" style={{ backgroundColor: theme.background.primary }}>
              <h3 className="text-xl font-bold mb-3 flex items-center gap-2" style={{ color: theme.text.primary }}>
                <FaChartLine className="text-green-500" />
                Track Your Progress
              </h3>
              <p style={{ color: theme.text.secondary }}>
                Monitor your ranking on leaderboards and earn badges for achievements
              </p>
            </div>
            
            <div className="p-6 rounded-xl" style={{ backgroundColor: theme.background.primary }}>
              <h3 className="text-xl font-bold mb-3 flex items-center gap-2" style={{ color: theme.text.primary }}>
                <FaFire className="text-orange-500" />
                Build Consistency
              </h3>
              <p style={{ color: theme.text.secondary }}>
                Maintain streaks and develop daily coding habits
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Target Audience */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: theme.text.primary }}>
              For All Skill Levels
            </h2>
            <p className="text-lg max-w-3xl mx-auto" style={{ color: theme.text.secondary }}>
              Whether you're a beginner learning programming, a student preparing for placements, 
              a competitive programmer, or a developer seeking regular practice — CodeBattles is designed for you.
            </p>
          </div>
          
          <div className="grid md:grid-cols-4 gap-4">
            <div className="text-center p-4">
              <FaUsers className="text-3xl text-blue-500 mx-auto mb-2" />
              <p className="font-semibold" style={{ color: theme.text.primary }}>Beginners</p>
            </div>
            <div className="text-center p-4">
              <FaUsers className="text-3xl text-green-500 mx-auto mb-2" />
              <p className="font-semibold" style={{ color: theme.text.primary }}>Students</p>
            </div>
            <div className="text-center p-4">
              <FaUsers className="text-3xl text-purple-500 mx-auto mb-2" />
              <p className="font-semibold" style={{ color: theme.text.primary }}>Competitive Coders</p>
            </div>
            <div className="text-center p-4">
              <FaUsers className="text-3xl text-orange-500 mx-auto mb-2" />
              <p className="font-semibold" style={{ color: theme.text.primary }}>Developers</p>
            </div>
          </div>
        </div>
      </section>

      {/* Learning + Competition Balance */}
      <section className="py-16 px-4" style={{ backgroundColor: theme.background.secondary }}>
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: theme.text.primary }}>
              Learn While Competing
            </h2>
            <p className="text-lg max-w-3xl mx-auto" style={{ color: theme.text.secondary }}>
              CodeBattles balances learning and competition. You don't just solve problems randomly — 
              you learn through structured challenges and competitive environments that push you to improve.
            </p>
          </div>
        </div>
      </section>

      {/* Skill Growth Focus */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: theme.text.primary }}>
              Focus on Skill Growth
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center p-6 rounded-xl" style={{ backgroundColor: theme.background.secondary }}>
              <FaFire className="text-4xl text-orange-500 mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-2" style={{ color: theme.text.primary }}>Consistency</h3>
              <p style={{ color: theme.text.secondary }}>Build daily coding habits with streaks and regular activity</p>
            </div>
            <div className="text-center p-6 rounded-xl" style={{ backgroundColor: theme.background.secondary }}>
              <FaBullseye className="text-4xl text-red-500 mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-2" style={{ color: theme.text.primary }}>Accuracy + Speed</h3>
              <p style={{ color: theme.text.secondary }}>Improve both correctness and efficiency in problem-solving</p>
            </div>
            <div className="text-center p-6 rounded-xl" style={{ backgroundColor: theme.background.secondary }}>
              <FaChartLine className="text-4xl text-green-500 mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-2" style={{ color: theme.text.primary }}>Progressive Difficulty</h3>
              <p style={{ color: theme.text.secondary }}>Advance from Easy to Medium to Hard problems at your pace</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="container mx-auto max-w-5xl text-center">
          {!loading && currentUser ? (
            <>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Continue Your Journey
              </h2>
              <p className="text-xl text-blue-100 mb-8">
                Keep improving your skills and climb the leaderboard
              </p>
              <div className="flex flex-col md:flex-row gap-4 justify-center">
                <a
                  href="/problems"
                  className="px-8 py-4 bg-white text-blue-600 text-lg font-semibold rounded-xl hover:bg-gray-100 transition-all"
                >
                  Solve Problems
                </a>
                <a
                  href="/challenges"
                  className="px-8 py-4 bg-transparent border-2 border-white text-white text-lg font-semibold rounded-xl hover:bg-white hover:text-blue-600 transition-all"
                >
                  Join Challenges
                </a>
              </div>
            </>
          ) : (
            <>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Ready to Start Your Journey?
              </h2>
              <p className="text-xl text-blue-100 mb-8">
                Join thousands of developers improving their skills on CodeBattles
              </p>
              <div className="flex flex-col md:flex-row gap-4 justify-center">
                <a
                  href="/register"
                  className="px-8 py-4 bg-white text-blue-600 text-lg font-semibold rounded-xl hover:bg-gray-100 transition-all"
                >
                  Get Started Free
                </a>
                <a
                  href="/problems"
                  className="px-8 py-4 bg-transparent border-2 border-white text-white text-lg font-semibold rounded-xl hover:bg-white hover:text-blue-600 transition-all"
                >
                  Browse Problems
                </a>
              </div>
            </>
          )}
        </div>
      </section>

      <Footer />
      <FeedbackSection />
    </div>
  );
};

export default About;
